import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from '../../i18n/useTranslation'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { usePopup } from '../../hooks/usePopup'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { userApi } from '../../api'
import type { UserPreferences, UpdateUserPreferencesRequest } from '../../types/api'
import { Avatar, Card, SettingsToggleRow, Button } from '../ui'

export default function MyProfilePage() {
  const theme = useTheme()
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const { showSuccess, showError } = usePopup()
  const { refreshPreferences } = useUserPreferences()

  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const loadPreferences = useCallback(async () => {
    try {
      const data = await userApi.getUserPreferences()
      setPreferences(data)
    } catch (error) {
      console.error('Error loading preferences:', error)
    }
  }, [])

  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  async function handlePhotoSyncToggle(value: boolean) {
    try {
      setSaving(true)
      const request: UpdateUserPreferencesRequest = { photoSyncEnabled: value }
      const updated = await userApi.updateUserPreferences(request)
      setPreferences(updated)
      await refreshPreferences()
      await refreshUser()
      showSuccess(t.preferences.saveSuccess)
    } catch (error) {
      console.error('Error saving photo sync preference:', error)
      showError(t.preferences.saveError)
    } finally {
      setSaving(false)
    }
  }

  async function handleSyncPhoto() {
    if (!photoSyncEnabled) {
      showError(t.preferences.userProfile.syncPhotoDisabled)
      return
    }

    try {
      setSyncing(true)
      const result = await userApi.syncPhoto()
      
      if (result.status === 'success') {
        await refreshUser()
        showSuccess(t.preferences.userProfile.syncPhotoSuccess)
      } else if (result.status === 'no_photo' || result.status === 'failed') {
        // Show error with Telegram privacy settings info
        showError(t.preferences.userProfile.syncPhotoFailedWithPrivacy)
      } else if (result.status === 'sync_disabled') {
        showError(t.preferences.userProfile.syncPhotoDisabled)
      } else {
        showError(result.message || t.preferences.userProfile.syncPhotoError)
      }
    } catch (err: unknown) {
      console.error('Error syncing photo:', err)
      const response = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { status?: string; message?: string } } }).response
        : undefined
      if (response?.data?.status === 'no_photo' || response?.data?.status === 'failed') {
        showError(t.preferences.userProfile.syncPhotoFailedWithPrivacy)
      } else {
        showError(response?.data?.message || t.preferences.userProfile.syncPhotoError)
      }
    } finally {
      setSyncing(false)
    }
  }

  if (!user) return null

  const displayName = user.firstName
    ? `${user.firstName} ${user.lastName ?? ''}`.trim()
    : user.username ?? '—'

  const photoSyncEnabled = preferences?.photoSyncEnabled ?? true

  return (
    <div className="p-5">
      <section aria-labelledby="profile-heading">
        <h1 id="profile-heading" className="text-2xl font-bold mb-6 opacity-90">
          {t.preferences.userProfile.title}
        </h1>
        <div className="flex items-start gap-5">
          <Avatar
            photoUrl={user.photoUrl}
            name={displayName}
            size="lg"
            fallbackBg={theme.brand.medium}
          />
          <div className="min-w-0 flex-1 pt-0.5 space-y-3">
            {user.firstName != null && user.firstName !== '' && (
              <div>
                <p className="text-xs opacity-60 mb-0.5">{t.preferences.userProfile.firstName}</p>
                <p className="font-semibold truncate">{user.firstName}</p>
              </div>
            )}
            {user.lastName != null && user.lastName !== '' && (
              <div>
                <p className="text-xs opacity-60 mb-0.5">{t.preferences.userProfile.lastName}</p>
                <p className="font-semibold truncate">{user.lastName}</p>
              </div>
            )}
            {user.username && (
              <div>
                <p className="text-xs opacity-60 mb-0.5">{t.preferences.userProfile.username}</p>
                <p className="truncate opacity-90">@{user.username}</p>
              </div>
            )}
            {!user.firstName && !user.lastName && !user.username && (
              <p className="text-sm opacity-60">{t.preferences.userProfile.noInfo}</p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-6" aria-labelledby="photo-sync-heading">
        <h2 id="photo-sync-heading" className="sr-only">
          {t.preferences.userProfile.photoSyncEnabled}
        </h2>
        <Card className="p-0 overflow-hidden">
          <div className="px-4">
            <SettingsToggleRow
              label={t.preferences.userProfile.photoSyncEnabled}
              description={t.preferences.userProfile.photoSyncDescription}
              checked={photoSyncEnabled}
              onChange={handlePhotoSyncToggle}
              disabled={saving}
              last={!photoSyncEnabled}
            />
            {photoSyncEnabled && (
              <div className="py-3 border-t border-gray-400/20">
                <Button
                  onClick={handleSyncPhoto}
                  disabled={syncing || saving}
                  variant="secondary"
                  size="md"
                  fullWidth
                >
                  {syncing ? t.preferences.userProfile.syncingPhoto : t.preferences.userProfile.syncPhotoNow}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </section>
    </div>
  )
}
